import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  CheckSquare, Clock, AlertTriangle, User, Building2,
  TrendingUp, Calendar, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Truncate long names to "Nombre + inicial del apellido" and keep full name for tooltip
const truncateName = (name) => {
  if (!name) return '—';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length <= 1) return parts[0];
  return `${parts[0]} ${parts[1][0]}.`;
};

const TasksAnalytics = ({ clientTypeFilter = ['typsa', 'privada', 'publica'], ownerFilter = 'me', ownerName = null, period: periodProp = 'all' }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const period = periodProp;

  useEffect(() => {
    fetchAnalytics();
  }, [period, clientTypeFilter, ownerFilter, ownerName]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams();
      params.append('period', period);
      if (clientTypeFilter && clientTypeFilter.length > 0) {
        clientTypeFilter.forEach(type => params.append('company_types', type));
      }
      // Owner: translate 'me' → current user name; 'unassigned' → __unassigned__; 'all' → omit.
      if (ownerFilter === 'me' && ownerName) {
        params.append('owner', ownerName);
      } else if (ownerFilter === 'unassigned') {
        params.append('owner', '__unassigned__');
      } else if (ownerFilter && ownerFilter !== 'all') {
        params.append('owner', ownerFilter);
      }
      const response = await axios.get(`${API}/analytics/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Cargando analíticas...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No se pudieron cargar las analíticas
      </div>
    );
  }

  const { summary, status_distribution, priority_distribution, by_assignee, weekly_trend, by_entity } = analytics;

  const statusVisible = (status_distribution || []).filter(d => d.value > 0);
  const statusSum = statusVisible.reduce((s, d) => s + d.value, 0);
  const statusValidation = statusVisible.map(d => d.value).join(' + ') + ` = ${summary.total}`;

  const priorityVisible = (priority_distribution || []).filter(d => d.value > 0);

  // Prepare by_assignee data with truncated labels + full name for tooltip
  const assigneeData = (by_assignee || []).map(a => ({
    ...a,
    fullName: a.name,
    displayName: truncateName(a.name),
    // Recompute mutually-exclusive breakdown at assignee level using top-level status_distribution ratios is
    // not accurate. Backend already returns pending/completed which are OK to keep as-is for the bar.
  }));
  const barHeight = Math.max(280, assigneeData.length * 42);

  return (
    <div className="space-y-6 p-4" data-testid="tasks-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card data-testid="tasks-kpi-total"><CardContent className="pt-4">
          <div className="flex items-center gap-2"><CheckSquare className="w-4 h-4 text-blue-500" /><span className="text-sm text-muted-foreground">Total</span></div>
          <p className="text-2xl font-bold mt-1">{summary.total}</p>
        </CardContent></Card>
        <Card data-testid="tasks-kpi-pending"><CardContent className="pt-4">
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-amber-500" /><span className="text-sm text-muted-foreground">Pendientes</span></div>
          <p className="text-2xl font-bold mt-1">{summary.pending}</p>
        </CardContent></Card>
        <Card data-testid="tasks-kpi-completed"><CardContent className="pt-4">
          <div className="flex items-center gap-2"><CheckSquare className="w-4 h-4 text-green-500" /><span className="text-sm text-muted-foreground">Completadas</span></div>
          <p className="text-2xl font-bold mt-1">{summary.completed}</p>
        </CardContent></Card>
        <Card data-testid="tasks-kpi-overdue"><CardContent className="pt-4">
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-sm text-muted-foreground">Vencidas</span></div>
          <p className="text-2xl font-bold mt-1">{summary.overdue}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-purple-500" /><span className="text-sm text-muted-foreground">% Completado</span></div>
          <p className="text-2xl font-bold mt-1">{summary.completion_rate}%</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-indigo-500" /><span className="text-sm text-muted-foreground">Esta semana</span></div>
          <p className="text-2xl font-bold mt-1">{summary.due_this_week}</p>
        </CardContent></Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Status Distribution (mutually exclusive) */}
        <Card data-testid="tasks-chart-status">
          <CardHeader><CardTitle className="text-base">Estado de Tareas</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusVisible}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    isAnimationActive={false}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {statusVisible.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-xs text-muted-foreground text-center" data-testid="status-validation">
              {statusValidation}
              {statusSum !== summary.total && (
                <span className="text-red-600 ml-2 font-medium">⚠ descuadre: suma={statusSum}, total={summary.total}</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card data-testid="tasks-chart-priority">
          <CardHeader><CardTitle className="text-base">Por Prioridad</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 260, minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityVisible}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    isAnimationActive={false}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {priorityVisible.map((entry, index) => (
                      <Cell key={`cell-p-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-xs text-muted-foreground text-center" data-testid="priority-validation">
              {priorityVisible.map(d => d.value).join(' + ')} = {summary.total}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card data-testid="tasks-chart-assignee">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4" />Por Responsable</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: barHeight, minHeight: barHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={assigneeData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis
                    dataKey="displayName"
                    type="category"
                    width={120}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    labelFormatter={(label, payload) => {
                      const p = payload && payload[0]?.payload;
                      return p?.fullName || label;
                    }}
                  />
                  <Legend />
                  <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pendientes" isAnimationActive={false} />
                  <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completadas" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="tasks-chart-weekly">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" />Tendencia Semanal</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 300, minHeight: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weekly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="created" stroke="#3b82f6" name="Creadas" strokeWidth={2} isAnimationActive={false} />
                  <Line type="monotone" dataKey="completed" stroke="#10b981" name="Completadas" strokeWidth={2} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Entity */}
      <Card data-testid="tasks-chart-entity">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Por Empresa/Unidad</CardTitle></CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 300, minHeight: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={by_entity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={80} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366f1" name="Tareas" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TasksAnalytics;
